import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Home, Wrench, Receipt, ShieldCheck, Calendar, Car, LogOut, ArrowLeft,
  ArrowRight, Plus, Phone, Mail, X, ChevronRight, Download, CheckCircle2,
  CreditCard, Wallet, Clock, AlertTriangle, FileText, Loader2, Sparkles,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: true });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("kk_portal_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

// ---------- Auth ----------
function saveToken(t) { if (t) localStorage.setItem("kk_portal_token", t); }
function clearToken() { localStorage.removeItem("kk_portal_token"); }
function hasToken() { return !!localStorage.getItem("kk_portal_token"); }

// ---------- Landing / Auth page ----------
function PortalAuth() {
  const [mode, setMode] = useState("login"); // login | signup | otp
  const [phone, setPhone] = useState("");
  const [pw, setPw] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [debugOtp, setDebugOtp] = useState(null);
  const nav = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/portal/signup/request-otp", { phone });
      if (data.debug_otp) setDebugOtp(data.debug_otp);
      toast.success("OTP sent. Enter the 6-digit code.");
      setMode("otp");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/portal/signup/verify", { phone, otp, name, email: email || undefined, password: pw });
      saveToken(data.token);
      toast.success(`Welcome, ${data.name}!`);
      nav("/portal/home", { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/portal/login", { phone, password: pw });
      saveToken(data.token);
      toast.success(`Welcome back, ${data.name || ""}`.trim());
      nav("/portal/home", { replace: true });
    } catch (err) { toast.error(err?.response?.data?.detail || "Login failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--kk-bg)] text-[var(--kk-black)]" data-testid="portal-auth">
      <div className="max-w-md mx-auto px-5 pt-10 pb-16">
        <div className="flex items-center gap-2 mb-10">
          <div className="w-9 h-9 rounded-sm bg-[var(--kk-black)] text-[var(--kk-lime)] flex items-center justify-center font-display">KK</div>
          <div>
            <div className="font-display text-xl tracking-wider uppercase leading-none">Karr Kraft</div>
            <div className="kk-overline text-[0.55rem] mt-0.5">Customer Portal</div>
          </div>
        </div>

        <h1 className="font-display text-4xl md:text-5xl uppercase leading-[0.95]">
          {mode === "login" ? "Sign in" : mode === "signup" ? "Create account" : "Enter OTP"}
        </h1>
        <p className="text-sm text-[var(--kk-muted)] mt-3 mb-8">
          {mode === "login" ? "Welcome back. Track your cars, service jobs and invoices in one place." : mode === "signup" ? "We'll text you a one-time code to verify your phone." : "We just sent a 6-digit code — valid for 10 minutes."}
        </p>

        {mode === "login" && (
          <form onSubmit={login} className="space-y-4" data-testid="portal-login-form">
            <div>
              <div className="kk-overline">Phone</div>
              <input className="kk-input" required value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98xxxxxxxx" data-testid="portal-phone" />
            </div>
            <div>
              <div className="kk-overline">Password</div>
              <input className="kk-input" required type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="portal-password" />
            </div>
            <button className="kk-btn-primary w-full justify-center" disabled={loading} data-testid="portal-login-submit">
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Sign in"}
            </button>
            <div className="text-center text-xs text-[var(--kk-muted)] pt-3">
              No account yet? <button type="button" className="underline font-semibold" onClick={() => setMode("signup")} data-testid="portal-switch-signup">Create one</button>
            </div>
          </form>
        )}

        {mode === "signup" && (
          <form onSubmit={requestOtp} className="space-y-4" data-testid="portal-signup-form">
            <div>
              <div className="kk-overline">Phone *</div>
              <input className="kk-input" required value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 98xxxxxxxx" data-testid="portal-signup-phone" />
            </div>
            <button className="kk-btn-primary w-full justify-center" disabled={loading} data-testid="portal-signup-submit">
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Send OTP"} <ArrowRight size={14} />
            </button>
            <div className="text-center text-xs text-[var(--kk-muted)] pt-3">
              Already have an account? <button type="button" className="underline font-semibold" onClick={() => setMode("login")}>Sign in</button>
            </div>
          </form>
        )}

        {mode === "otp" && (
          <form onSubmit={verify} className="space-y-4" data-testid="portal-otp-form">
            <div>
              <div className="kk-overline">6-digit OTP</div>
              <input className="kk-input tracking-[0.4em] text-xl text-center font-mono" required value={otp}
                maxLength={6} onChange={(e) => setOtp(e.target.value)} placeholder="••••••" data-testid="portal-otp-input" autoFocus />
              {debugOtp && (
                <div className="text-[0.6rem] text-[var(--kk-muted)] mt-1">Dev hint: {debugOtp}</div>
              )}
            </div>
            <div>
              <div className="kk-overline">Your name *</div>
              <input className="kk-input" required value={name} onChange={(e) => setName(e.target.value)} data-testid="portal-name" />
            </div>
            <div>
              <div className="kk-overline">Email (optional)</div>
              <input className="kk-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-email" />
            </div>
            <div>
              <div className="kk-overline">Create password *</div>
              <input className="kk-input" required type="password" minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="portal-new-password" />
            </div>
            <button className="kk-btn-primary w-full justify-center" disabled={loading} data-testid="portal-verify">
              {loading ? <Loader2 className="animate-spin" size={16} /> : "Verify & create account"}
            </button>
            <button type="button" className="text-xs text-center w-full underline text-[var(--kk-muted)]" onClick={() => setMode("signup")}>
              ← Change phone number
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------- Shell ----------
const TABS = [
  { to: "/portal/home", label: "Home", icon: Home },
  { to: "/portal/services", label: "Services", icon: Wrench },
  { to: "/portal/invoices", label: "Bills", icon: Receipt },
  { to: "/portal/warranties", label: "Warranty", icon: ShieldCheck },
  { to: "/portal/book", label: "Book", icon: Calendar },
];

function PortalShell() {
  const [me, setMe] = useState(null);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    if (!hasToken()) { nav("/portal", { replace: true }); return; }
    api.get("/portal/me").then(({ data }) => setMe(data)).catch(() => {
      clearToken();
      nav("/portal", { replace: true });
    });
  }, [nav]);

  const logout = () => { clearToken(); toast.success("Signed out."); nav("/portal"); };

  if (!me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--kk-bg)]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--kk-bg)] text-[var(--kk-black)] pb-20">
      <header className="sticky top-0 z-40 bg-[var(--kk-surface)] border-b border-[var(--kk-border)]">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-sm bg-[var(--kk-black)] text-[var(--kk-lime)] flex items-center justify-center font-display text-xs">KK</div>
            <div>
              <div className="font-display text-sm tracking-wider uppercase leading-none">Karr Kraft</div>
              <div className="kk-overline text-[0.5rem] mt-0.5">{me.name}</div>
            </div>
          </div>
          <button onClick={logout} className="kk-pill kk-pill-muted" data-testid="portal-logout">
            <LogOut size={11} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-5">
        <Outlet context={{ me }} />
      </main>

      <nav className="fixed bottom-0 inset-x-0 bg-[var(--kk-surface)] border-t border-[var(--kk-border)] z-40">
        <div className="max-w-2xl mx-auto grid grid-cols-5">
          {TABS.map((t) => {
            const active = loc.pathname.startsWith(t.to);
            return (
              <NavLink key={t.to} to={t.to} data-testid={`portal-tab-${t.label.toLowerCase()}`}
                className={"relative flex flex-col items-center justify-center py-2.5 text-[0.6rem] uppercase tracking-wider " +
                  (active ? "text-[var(--kk-black)] font-bold" : "text-[var(--kk-muted)]")}>
                <t.icon size={17} />
                <span className="mt-0.5">{t.label}</span>
                {active && <span className="absolute top-0 w-8 h-0.5 bg-[var(--kk-lime)]" />}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

// ---------- Pages ----------
function TopTitle({ kicker, title, subtitle }) {
  return (
    <div className="mb-6">
      <div className="kk-overline">{kicker}</div>
      <h1 className="font-display text-3xl md:text-4xl uppercase leading-[0.95] mt-1">{title}</h1>
      {subtitle && <p className="text-xs text-[var(--kk-muted)] mt-2">{subtitle}</p>}
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent, onClick, test }) {
  return (
    <button onClick={onClick} data-testid={test}
      className="text-left p-4 bg-[var(--kk-surface)] border border-[var(--kk-border)] border-l-[3px] hover:border-[var(--kk-black)] transition"
      style={{ borderLeftColor: accent || "var(--kk-border)" }}>
      <div className="flex items-center justify-between">
        <Icon size={14} className="text-[var(--kk-muted)]" />
        <ChevronRight size={13} className="text-[var(--kk-muted)]" />
      </div>
      <div className="mt-3 font-display text-2xl leading-none">{value}</div>
      <div className="mt-2 text-[0.6rem] uppercase tracking-[0.14em] text-[var(--kk-muted)]">{label}</div>
    </button>
  );
}

function PortalHome() {
  const [summary, setSummary] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [odoFor, setOdoFor] = useState(null);
  const [odoKm, setOdoKm] = useState("");
  const nav = useNavigate();
  const load = () => Promise.all([api.get("/portal/summary"), api.get("/portal/reminders")])
    .then(([s, r]) => { setSummary(s.data); setReminders(r.data); });
  useEffect(() => { load(); }, []);
  const saveOdo = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/portal/vehicles/${odoFor.car_id}/odometer`, { current_km: Number(odoKm) });
      toast.success("Odometer updated");
      setOdoFor(null); setOdoKm("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  if (!summary) return <Loader2 className="animate-spin mx-auto mt-10" />;
  return (
    <div>
      <TopTitle kicker="Your garage" title="Hello." subtitle="Everything about your cars, in one tap." />
      <div className="grid grid-cols-2 gap-3">
        <StatCard label="Active jobs" value={summary.active_job_cards} icon={Wrench} accent="var(--kk-lime)"
          onClick={() => nav("/portal/services")} test="home-jobs" />
        <StatCard label="Bills due" value={summary.pending_invoices} icon={Receipt} accent="var(--kk-warning)"
          onClick={() => nav("/portal/invoices")} test="home-bills" />
        <StatCard label="Active warranties" value={summary.warranties_active} icon={ShieldCheck} accent="var(--kk-black)"
          onClick={() => nav("/portal/warranties")} test="home-warranties" />
        <StatCard label="Service reminders" value={summary.service_reminders} icon={Clock}
          accent={summary.service_reminders > 0 ? "var(--kk-danger)" : "var(--kk-border)"}
          onClick={() => nav("/portal/book")} test="home-reminders" />
      </div>

      {reminders.length > 0 && (
        <div className="mt-8 kk-card p-4" data-testid="home-reminders-block">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle size={14} className="text-[var(--kk-danger)]" />
            <div className="font-display text-sm uppercase">Service due</div>
          </div>
          <ul className="space-y-2">
            {reminders.map((r, i) => (
              <li key={i} className="flex items-center justify-between border-b border-[var(--kk-border)] pb-2 last:border-0">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-sm truncate">{r.label || "Your car"}</div>
                  <div className="text-xs text-[var(--kk-muted)]">{r.registration_no} · {r.reason}</div>
                  {r.current_km != null && (
                    <div className="text-[0.6rem] text-[var(--kk-muted)] mt-0.5 font-mono">
                      {r.current_km.toLocaleString("en-IN")} km
                      {r.last_service_km ? ` · last @ ${r.last_service_km.toLocaleString("en-IN")}` : ""}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 items-end ml-2">
                  <button onClick={() => nav("/portal/book")} className="kk-pill kk-pill-lime" data-testid={`reminder-book-${i}`}>Book</button>
                  <button onClick={() => { setOdoFor(r); setOdoKm(r.current_km || ""); }} className="kk-pill kk-pill-muted text-[0.55rem]" data-testid={`reminder-odo-${i}`}>Log km</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-sm uppercase">Recent service</div>
          <button className="text-xs underline" onClick={() => nav("/portal/services")}>All</button>
        </div>
        {(summary.recent_job_cards || []).length === 0 ? (
          <div className="text-sm text-[var(--kk-muted)] py-6 text-center border border-dashed border-[var(--kk-border)]">
            No service records yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {summary.recent_job_cards.map((j) => (
              <li key={j.id} onClick={() => nav(`/portal/services/${j.id}`)}
                className="p-3 bg-[var(--kk-surface)] border border-[var(--kk-border)] hover:border-[var(--kk-black)] cursor-pointer transition"
                data-testid={`home-jc-${j.id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-mono text-[0.6rem] text-[var(--kk-muted)]">{j.code}</div>
                    <div className="font-semibold text-sm truncate">{j.make} {j.model} · {j.registration_no}</div>
                  </div>
                  <span className="kk-pill kk-pill-muted text-[0.55rem]">{(j.status || "").replace(/_/g, " ")}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8 grid grid-cols-2 gap-3">
        <button onClick={() => nav("/portal/book")} className="kk-btn-primary justify-center" data-testid="cta-book">
          <Plus size={14} /> Book service
        </button>
        <button onClick={() => nav("/portal/showroom")} className="kk-btn-ghost justify-center" data-testid="cta-showroom">
          <Car size={14} /> Browse cars
        </button>
      </div>

      {odoFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-3" onClick={() => setOdoFor(null)}>
          <form onSubmit={saveOdo} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[var(--kk-surface)] p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="kk-overline">Log odometer</div>
                <div className="font-display text-xl uppercase">{odoFor.label}</div>
                <div className="text-xs text-[var(--kk-muted)]">{odoFor.registration_no}</div>
              </div>
              <button type="button" onClick={() => setOdoFor(null)}><X /></button>
            </div>
            <div>
              <div className="kk-overline">Current reading (km)</div>
              <input className="kk-input text-lg font-mono" required type="number" min={odoFor.last_service_km || 0}
                value={odoKm} onChange={(e) => setOdoKm(e.target.value)} data-testid="odo-km" autoFocus />
              {odoFor.last_service_km && (
                <div className="text-[0.65rem] text-[var(--kk-muted)] mt-1">Last service was at {odoFor.last_service_km.toLocaleString("en-IN")} km</div>
              )}
            </div>
            <button className="kk-btn-primary w-full justify-center mt-4" data-testid="odo-save">
              <CheckCircle2 size={14}/> Save
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- Services (list + detail) ----------
function ServicesList() {
  const [list, setList] = useState([]);
  const nav = useNavigate();
  useEffect(() => { api.get("/portal/job-cards").then(({ data }) => setList(data)); }, []);
  return (
    <div>
      <TopTitle kicker="Service" title="Your job cards" subtitle="Current and past workshop visits." />
      {list.length === 0 ? (
        <div className="text-sm text-[var(--kk-muted)] py-10 text-center border border-dashed border-[var(--kk-border)]">
          No job cards yet. <button className="underline font-semibold" onClick={() => nav("/portal/book")}>Book a service</button>
        </div>
      ) : (
        <ul className="space-y-2">
          {list.map((j) => (
            <li key={j.id} onClick={() => nav(`/portal/services/${j.id}`)}
              className="p-4 bg-[var(--kk-surface)] border border-[var(--kk-border)] hover:border-[var(--kk-black)] cursor-pointer transition"
              data-testid={`jc-row-${j.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-[0.6rem] text-[var(--kk-muted)]">{j.code}</div>
                  <div className="font-semibold text-sm">{j.make} {j.model}</div>
                  <div className="text-xs text-[var(--kk-muted)]">{j.registration_no} · {fmtDate(j.created_at)}</div>
                </div>
                <span className="kk-pill kk-pill-muted text-[0.55rem]">{(j.status || "").replace(/_/g, " ")}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function JobCardDetail() {
  const id = window.location.pathname.split("/").pop();
  const nav = useNavigate();
  const [jc, setJc] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [debugOtp, setDebugOtp] = useState(null);
  const [otp, setOtp] = useState("");
  const load = () => api.get(`/portal/job-cards/${id}`).then(({ data }) => setJc(data));
  useEffect(() => { load(); }, [id]);

  const requestOtp = async () => {
    try {
      const { data } = await api.post(`/portal/job-cards/${id}/request-otp`);
      setOtpSent(true);
      if (data.debug_otp) setDebugOtp(data.debug_otp);
      toast.success("OTP sent to your phone");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const approve = async () => {
    try {
      await api.post(`/portal/job-cards/${id}/approve`, { otp });
      toast.success("Estimate approved — work will begin.");
      setOtp(""); setOtpSent(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid OTP"); }
  };

  if (!jc) return <Loader2 className="animate-spin mx-auto mt-10" />;
  const needsApproval = jc.status === "pending_approval" || jc.status === "draft";
  const parts = jc.parts || [];
  const labour = jc.labour_lines || jc.labor_lines || [];

  return (
    <div>
      <button className="text-xs underline mb-3" onClick={() => nav(-1)}><ArrowLeft size={11}/> Back</button>
      <TopTitle kicker={jc.code} title={`${jc.make || ""} ${jc.model || ""}`.trim() || "Job card"} subtitle={`${jc.registration_no || ""} · ${fmtDateTime(jc.created_at)}`} />
      <div className="flex items-center gap-2 mb-4">
        <span className="kk-pill kk-pill-muted">{(jc.status || "").replace(/_/g, " ").toUpperCase()}</span>
        {jc.customer_approved_at && <span className="kk-pill kk-pill-lime"><CheckCircle2 size={11}/> Approved {fmtDate(jc.customer_approved_at)}</span>}
      </div>

      {jc.complaints && (
        <div className="kk-card p-4 mb-3">
          <div className="kk-overline mb-1">Complaints</div>
          <p className="text-sm whitespace-pre-wrap">{jc.complaints}</p>
        </div>
      )}
      {jc.diagnosis && (
        <div className="kk-card p-4 mb-3">
          <div className="kk-overline mb-1">Diagnosis</div>
          <p className="text-sm whitespace-pre-wrap">{jc.diagnosis}</p>
        </div>
      )}

      {(parts.length > 0 || labour.length > 0) && (
        <div className="kk-card p-4 mb-3">
          <div className="kk-overline mb-2">Estimate</div>
          <ul className="text-sm space-y-1.5">
            {parts.map((p, i) => (
              <li key={`p${i}`} className="flex justify-between border-b border-[var(--kk-border)] pb-1.5 last:border-0">
                <span className="truncate">Part · {p.name || p.sku}</span>
                <span className="font-mono">{fmtINR((p.qty || 1) * (p.rate || p.price || 0))}</span>
              </li>
            ))}
            {labour.map((l, i) => (
              <li key={`l${i}`} className="flex justify-between border-b border-[var(--kk-border)] pb-1.5 last:border-0">
                <span className="truncate">Labour · {l.description}</span>
                <span className="font-mono">{fmtINR((l.hours || 1) * (l.rate || 0))}</span>
              </li>
            ))}
          </ul>
          {typeof jc.total === "number" && (
            <div className="flex justify-between mt-3 pt-3 border-t border-[var(--kk-border)]">
              <span className="kk-overline">Total</span>
              <span className="font-display text-lg">{fmtINR(jc.total)}</span>
            </div>
          )}
        </div>
      )}

      {needsApproval && (
        <div className="kk-card p-4 mb-3 border-l-4 border-l-[var(--kk-lime)]" data-testid="jc-approval-block">
          <div className="kk-overline mb-1">Your approval needed</div>
          <p className="text-sm text-[var(--kk-muted)] mb-3">Approve this estimate to unlock work at our workshop.</p>
          {!otpSent ? (
            <button className="kk-btn-primary w-full justify-center" onClick={requestOtp} data-testid="jc-request-otp">
              <Phone size={14}/> Send me an OTP to approve
            </button>
          ) : (
            <div className="space-y-2">
              <input className="kk-input tracking-[0.4em] font-mono text-center" value={otp} maxLength={6}
                onChange={(e) => setOtp(e.target.value)} placeholder="••••••" data-testid="jc-otp-input" />
              {debugOtp && <div className="text-[0.6rem] text-[var(--kk-muted)]">Dev hint: {debugOtp}</div>}
              <button className="kk-btn-primary w-full justify-center" onClick={approve} data-testid="jc-approve">
                <CheckCircle2 size={14}/> Approve estimate
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Invoices ----------
function InvoicesList() {
  const [list, setList] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [amt, setAmt] = useState(0);
  const [mode, setMode] = useState("upi");
  const load = () => api.get("/portal/invoices").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);

  const downloadPdf = async (inv) => {
    try {
      const res = await api.get(`/portal/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `invoice-${inv.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
    } catch { toast.error("Could not download PDF"); }
  };

  const openPay = (inv) => {
    const bal = Math.max((inv.grand_total || 0) - (inv.amount_paid || 0), 0);
    setPayFor(inv); setAmt(bal); setMode("upi");
  };

  const payFull = async (inv) => {
    try {
      const fd = new FormData();
      const { data } = await api.post(`/portal/invoices/${inv.id}/razorpay/order`, fd);
      if (data.mock) {
        const vfd = new FormData();
        vfd.append("razorpay_order_id", data.order_id);
        vfd.append("razorpay_payment_id", "pay_MOCK_" + Math.random().toString(36).slice(2, 10));
        await api.post(`/portal/invoices/${inv.id}/razorpay/verify`, vfd);
        toast.success("Paid (mock). Configure Razorpay keys for live.");
        load();
        return;
      }
      const options = {
        key: data.key_id, amount: data.amount, currency: "INR",
        name: "Karr Kraft", description: inv.code, order_id: data.order_id,
        handler: async (res) => {
          const vfd = new FormData();
          vfd.append("razorpay_order_id", res.razorpay_order_id);
          vfd.append("razorpay_payment_id", res.razorpay_payment_id);
          vfd.append("razorpay_signature", res.razorpay_signature);
          await api.post(`/portal/invoices/${inv.id}/razorpay/verify`, vfd);
          toast.success("Payment captured"); load();
        },
        prefill: { name: inv.customer_name, contact: inv.customer_phone },
        theme: { color: "#CCFF00" },
      };
      if (!window.Razorpay) { toast.error("Razorpay SDK not loaded"); return; }
      new window.Razorpay(options).open();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
  };

  const partialPay = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append("amount", String(amt)); fd.append("mode", mode);
      const { data } = await api.post(`/portal/invoices/${payFor.id}/partial`, fd);
      toast.success(data.status === "paid" ? "Invoice fully settled" : `Part payment recorded · ₹${Math.round(data.balance_due)} remaining`);
      setPayFor(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <TopTitle kicker="Bills" title="Invoices" subtitle="Download PDFs, pay online, track outstanding." />
      {list.length === 0 ? (
        <div className="text-sm text-[var(--kk-muted)] py-10 text-center border border-dashed border-[var(--kk-border)]">No invoices yet.</div>
      ) : (
        <ul className="space-y-2">
          {list.map((i) => {
            const bal = Math.max((i.grand_total || 0) - (i.amount_paid || 0), 0);
            return (
              <li key={i.id} className="p-4 bg-[var(--kk-surface)] border border-[var(--kk-border)]" data-testid={`inv-row-${i.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[0.6rem] text-[var(--kk-muted)]">{i.code}</div>
                    <div className="font-semibold text-sm">{i.vehicle_reg || i.customer_name}</div>
                    <div className="text-xs text-[var(--kk-muted)]">{fmtDate(i.created_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-base">{fmtINR(i.grand_total)}</div>
                    {bal > 0 && <div className="text-[0.6rem] text-red-600">Due {fmtINR(bal)}</div>}
                    <span className={"kk-pill text-[0.5rem] " + (i.status === "paid" ? "kk-pill-lime" : "kk-pill-muted")}>{i.status}</span>
                  </div>
                </div>
                <div className="flex gap-1.5 mt-3 flex-wrap">
                  <button onClick={() => downloadPdf(i)} className="kk-pill kk-pill-muted" data-testid={`inv-pdf-${i.id}`}><FileText size={11}/> PDF</button>
                  {i.status !== "paid" && (
                    <>
                      <button onClick={() => payFull(i)} className="kk-pill kk-pill-black" data-testid={`inv-payfull-${i.id}`}><CreditCard size={11}/> Pay {fmtINR(bal)}</button>
                      <button onClick={() => openPay(i)} className="kk-pill kk-pill-lime" data-testid={`inv-part-${i.id}`}><Wallet size={11}/> Part-pay</button>
                    </>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {payFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-3" onClick={() => setPayFor(null)}>
          <form onSubmit={partialPay} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[var(--kk-surface)] p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="kk-overline">Part payment</div>
                <div className="font-display text-xl uppercase">{payFor.code}</div>
              </div>
              <button type="button" onClick={() => setPayFor(null)}><X /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
              <div><div className="kk-overline text-[0.5rem]">Total</div><div className="font-mono">{fmtINR(payFor.grand_total)}</div></div>
              <div><div className="kk-overline text-[0.5rem]">Paid</div><div className="font-mono">{fmtINR(payFor.amount_paid || 0)}</div></div>
              <div><div className="kk-overline text-[0.5rem]">Due</div><div className="font-mono text-red-600">{fmtINR(Math.max((payFor.grand_total || 0) - (payFor.amount_paid || 0), 0))}</div></div>
            </div>
            <div className="space-y-3">
              <div>
                <div className="kk-overline">Amount (INR)</div>
                <input className="kk-input" type="number" required min="0.01"
                  max={(payFor.grand_total || 0) - (payFor.amount_paid || 0)}
                  value={amt} onChange={(e) => setAmt(e.target.value)} data-testid="portal-pp-amt" />
              </div>
              <div>
                <div className="kk-overline">Mode</div>
                <select className="kk-input" value={mode} onChange={(e) => setMode(e.target.value)}>
                  <option value="upi">UPI</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="bank_transfer">Bank transfer</option>
                </select>
              </div>
            </div>
            <button className="kk-btn-primary w-full justify-center mt-5" data-testid="portal-pp-save">
              <Wallet size={14} /> Record payment
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- Warranties ----------
function WarrantyList() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/portal/warranties").then(({ data }) => setList(data)); }, []);
  const downloadCert = async (w) => {
    try {
      const res = await api.get(`/portal/warranties/${w.id}/certificate.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `warranty-${w.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 800);
    } catch { toast.error("Could not download certificate"); }
  };
  return (
    <div>
      <TopTitle kicker="Cover" title="Your warranties" subtitle="Live status, covered parts, signed certificates." />
      {list.length === 0 ? (
        <div className="text-sm text-[var(--kk-muted)] py-10 text-center border border-dashed border-[var(--kk-border)]">No active warranties.</div>
      ) : (
        <ul className="space-y-2">
          {list.map((w) => {
            const daysLeft = Math.floor((new Date(w.end_date) - new Date()) / (1000 * 60 * 60 * 24));
            const isExpired = daysLeft < 0;
            return (
              <li key={w.id} className="p-4 bg-[var(--kk-surface)] border border-[var(--kk-border)]" data-testid={`war-row-${w.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-[0.6rem] text-[var(--kk-muted)]">{w.code}</div>
                    <div className="font-semibold text-sm">{w.title || (w.type || "").replace(/_/g, " ")}</div>
                    <div className="text-xs text-[var(--kk-muted)]">{w.vehicle_reg} · valid till {fmtDate(w.end_date)}</div>
                  </div>
                  <div className="text-right">
                    <div className={"font-display text-lg " + (isExpired ? "text-red-600" : daysLeft <= 30 ? "text-amber-600" : "")}>{isExpired ? "Expired" : `${daysLeft}d`}</div>
                    <span className={"kk-pill text-[0.5rem] " + (w.status === "active" ? "kk-pill-lime" : "kk-pill-muted")}>{w.status}</span>
                  </div>
                </div>
                {(w.covered_items || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {w.covered_items.slice(0, 6).map((it, idx) => (
                      <span key={idx} className="kk-pill kk-pill-muted text-[0.5rem]">{it.name || it.sku}</span>
                    ))}
                    {w.covered_items.length > 6 && <span className="kk-pill kk-pill-muted text-[0.5rem]">+{w.covered_items.length - 6}</span>}
                  </div>
                )}
                <div className="flex gap-1.5 mt-3">
                  <button onClick={() => downloadCert(w)} className="kk-pill kk-pill-black" data-testid={`war-cert-${w.id}`}><Download size={11}/> Certificate</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------- Book (service booking + showroom entry) ----------
function BookFlow() {
  const [tab, setTab] = useState("service");
  const [vehicles, setVehicles] = useState([]);
  const [carId, setCarId] = useState("");
  const [reg, setReg] = useState("");
  const [date, setDate] = useState("");
  const [issue, setIssue] = useState("");
  const [history, setHistory] = useState([]);
  const nav = useNavigate();

  const loadHistory = () => api.get("/portal/service-requests").then(({ data }) => setHistory(data));

  useEffect(() => {
    api.get("/portal/vehicles").then(({ data }) => setVehicles(data));
    loadHistory();
  }, []);

  const cancel = async (id) => {
    if (!window.confirm("Cancel this request?")) return;
    try {
      await api.post(`/portal/service-requests/${id}/cancel`);
      toast.success("Cancelled");
      loadHistory();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/portal/service-requests", {
        car_id: carId || null,
        registration_no: reg || null,
        preferred_date: date,
        issue,
        type: tab,
      });
      toast.success(tab === "service" ? "Service request submitted — our team will call you." : "Callback requested — we'll ring you shortly.");
      setCarId(""); setReg(""); setDate(""); setIssue("");
      loadHistory();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <TopTitle kicker="Book" title="Book a slot" subtitle="Service booking, callback, or browse our showroom." />
      <div className="kk-tabs mb-4">
        <button onClick={() => setTab("service")} className={"kk-tab " + (tab === "service" ? "kk-tab-active" : "")} data-testid="book-tab-service">Service</button>
        <button onClick={() => setTab("callback")} className={"kk-tab " + (tab === "callback" ? "kk-tab-active" : "")} data-testid="book-tab-callback">Callback</button>
      </div>

      <form onSubmit={submit} className="space-y-3 kk-card p-4" data-testid="book-form">
        {tab === "service" && (
          <>
            <div>
              <div className="kk-overline">Select vehicle</div>
              <select className="kk-input" value={carId} onChange={(e) => { setCarId(e.target.value); const v = vehicles.find(x => x.id === e.target.value); setReg(v?.registration_no || ""); }}>
                <option value="">{"Not listed — I'll type the number"}</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{`${v.make || ""} ${v.model || ""} · ${v.registration_no || ""}`.trim()}</option>
                ))}
              </select>
            </div>
            {!carId && (
              <div>
                <div className="kk-overline">Registration #</div>
                <input className="kk-input" value={reg} onChange={(e) => setReg(e.target.value)} data-testid="book-reg" />
              </div>
            )}
            <div>
              <div className="kk-overline">Preferred date *</div>
              <input className="kk-input" required type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-date" />
            </div>
          </>
        )}
        {tab === "callback" && (
          <div>
            <div className="kk-overline">When should we call? *</div>
            <input className="kk-input" required type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="book-callback-date" />
          </div>
        )}
        <div>
          <div className="kk-overline">{tab === "service" ? "What's the issue? *" : "What should we discuss? *"}</div>
          <textarea className="kk-input min-h-[80px]" required value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="book-issue"
            placeholder={tab === "service" ? "Oil change + squeaky brakes" : "Pricing on BMW M3"} />
        </div>
        <button className="kk-btn-primary w-full justify-center" data-testid="book-submit">
          <Sparkles size={14}/> Submit request
        </button>
      </form>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-sm uppercase">Your requests</div>
          <button className="text-xs underline" onClick={() => nav("/portal/showroom")}>Browse cars →</button>
        </div>
        {history.length === 0 ? (
          <div className="text-sm text-[var(--kk-muted)] py-6 text-center border border-dashed border-[var(--kk-border)]">
            No requests yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {history.map((h) => (
              <li key={h.id} className="p-3 bg-[var(--kk-surface)] border border-[var(--kk-border)] text-xs" data-testid={`sr-row-${h.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm">{h.type === "callback" ? "Callback" : "Service"} · {h.preferred_date}</span>
                  <span className={"kk-pill text-[0.5rem] " + (h.status === "pending" ? "kk-pill-muted" : h.status === "cancelled" ? "kk-pill-muted" : "kk-pill-lime")}>{h.status}</span>
                </div>
                <div className="text-[var(--kk-muted)] mt-1 truncate">{h.issue}</div>
                {h.status === "pending" && (
                  <button onClick={() => cancel(h.id)} className="kk-pill kk-pill-muted mt-2 text-[0.55rem]" data-testid={`sr-cancel-${h.id}`}>
                    Cancel
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ---------- Showroom browse + test drive ----------
function ShowroomBrowse() {
  const [cars, setCars] = useState([]);
  const [picked, setPicked] = useState(null);
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const nav = useNavigate();
  useEffect(() => { api.get("/portal/showroom/cars").then(({ data }) => setCars(data)); }, []);

  const book = async (e) => {
    e.preventDefault();
    try {
      await api.post("/portal/test-drives", { car_id: picked.id, scheduled_at: when, notes });
      toast.success("Test drive booked. We'll confirm over phone.");
      setPicked(null); setWhen(""); setNotes("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <button className="text-xs underline mb-3" onClick={() => nav(-1)}><ArrowLeft size={11}/> Back</button>
      <TopTitle kicker="Showroom" title="Our stock" subtitle="Available vehicles. Tap to book a test drive." />
      {cars.length === 0 ? (
        <div className="text-sm text-[var(--kk-muted)] py-10 text-center border border-dashed border-[var(--kk-border)]">No cars listed right now.</div>
      ) : (
        <ul className="space-y-3">
          {cars.map((c) => (
            <li key={c.id} className="p-4 bg-[var(--kk-surface)] border border-[var(--kk-border)]" data-testid={`car-row-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base uppercase">{c.make} {c.model}</div>
                  <div className="text-xs text-[var(--kk-muted)]">{c.year} · {c.kms ? `${c.kms} km` : "Fresh"} · {c.fuel || "—"}</div>
                  {c.price && <div className="font-mono mt-1">{fmtINR(c.price)}</div>}
                </div>
                <span className={"kk-pill text-[0.55rem] " + (c.status === "available" ? "kk-pill-lime" : "kk-pill-muted")}>{c.status}</span>
              </div>
              <button onClick={() => setPicked(c)} className="kk-pill kk-pill-black mt-3" data-testid={`car-td-${c.id}`}>
                <Calendar size={11}/> Book test drive
              </button>
            </li>
          ))}
        </ul>
      )}

      {picked && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-3" onClick={() => setPicked(null)}>
          <form onSubmit={book} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-[var(--kk-surface)] p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="kk-overline">Test drive</div>
                <div className="font-display text-xl uppercase">{picked.make} {picked.model}</div>
              </div>
              <button type="button" onClick={() => setPicked(null)}><X /></button>
            </div>
            <div className="space-y-3">
              <div>
                <div className="kk-overline">When works best? *</div>
                <input type="datetime-local" required className="kk-input" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="td-when" />
              </div>
              <div>
                <div className="kk-overline">Any notes</div>
                <textarea className="kk-input min-h-[60px]" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="I'd like to check the interior cosmetics" />
              </div>
            </div>
            <button className="kk-btn-primary w-full justify-center mt-4" data-testid="td-submit">
              <Sparkles size={14}/> Confirm booking
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------- Root Portal component ----------
export default function Portal() {
  return (
    <Routes>
      <Route index element={hasToken() ? <Navigate to="/portal/home" replace /> : <PortalAuth />} />
      <Route element={<PortalShell />}>
        <Route path="home" element={<PortalHome />} />
        <Route path="services" element={<ServicesList />} />
        <Route path="services/:id" element={<JobCardDetail />} />
        <Route path="invoices" element={<InvoicesList />} />
        <Route path="warranties" element={<WarrantyList />} />
        <Route path="book" element={<BookFlow />} />
        <Route path="showroom" element={<ShowroomBrowse />} />
      </Route>
      <Route path="*" element={<Navigate to="/portal" replace />} />
    </Routes>
  );
}
